import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Paper,
    Snackbar,
    Stack,
    Typography
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'utils/axios';

async function requestReset(email) {
    const res = await axios.post(`password/forgot/`, { email });
    return res;
}

const validationSchema = Yup.object({
    email: Yup.string().email('Enter a valid email').required('Email is required')
});

export default function ForgotPasswordPage() {
    const [snack, setSnack] = useState(null);

    return (
        <>
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper elevation={1} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
                    <Stack spacing={2}>
                        <Typography variant="h4" fontWeight={700}>
                            Forgot your password?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Enter your account email and we’ll send you a link to reset your password.
                        </Typography>

                        <Formik
                            initialValues={{ email: '' }}
                            validationSchema={validationSchema}
                            onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                                const { status, data } = await requestReset(values.email);
                                setSubmitting(false);
                                if (status === 200) {
                                    setSnack({
                                        open: true,
                                        message: data?.message || 'If the email exists, a reset link will be sent.',
                                        severity: 'success'
                                    });
                                    resetForm();
                                } else {
                                    const apiError = data?.error || 'Something went wrong. Please try again.';
                                    setSnack({ open: true, message: apiError, severity: 'error' });
                                    // Field error fallbacks
                                    if (data?.email) setErrors({ email: data.email });
                                }
                            }}
                        >
                            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                                <form onSubmit={handleSubmit} noValidate>
                                    <Stack spacing={2.5}>
                                        <Box>
                                            <InputLabel htmlFor="email">Email</InputLabel>
                                            <OutlinedInput
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                fullWidth
                                                placeholder="your@email.com"
                                                error={Boolean(touched.email && errors.email)}
                                            />
                                            {touched.email && errors.email && <FormHelperText error>{errors.email}</FormHelperText>}
                                        </Box>

                                        <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                                            {isSubmitting ? 'Sending…' : 'Send reset link'}
                                        </Button>

                                        <Button href="/login" size="large" disabled={isSubmitting}>
                                            Return to Log in
                                        </Button>
                                    </Stack>
                                </form>
                            )}
                        </Formik>
                    </Stack>
                </Paper>

                <Snackbar
                    open={!!snack?.open}
                    autoHideDuration={4000}
                    onClose={() => setSnack(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {snack ? (
                        <Alert onClose={() => setSnack(null)} severity={snack.severity} sx={{ width: '100%' }}>
                            {snack.message}
                        </Alert>
                    ) : null}
                </Snackbar>
            </Container>
        </>
    );
}
